import React, { useEffect, useState } from 'react';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { AdCard, useAds } from '../../components/ads/AdCard';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Search, ShoppingCart } from 'lucide-react';

export const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { ads, loading: adsLoading } = useAds('marketplace');

  useEffect(() => {
    publicAPI.getProducts()
      .then(res => setProducts(res.data))
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));
  }, []);

  const buy = async (product) => {
    if (!window.confirm(`Purchase ${product.name} for ${product.price} ${product.currency}?`)) return;
    try {
      await userAPI.createOrder([{ product_id: product.id, quantity: 1 }]);
      toast.success('Purchase complete!');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Purchase failed');
    }
  };

  const filtered = products.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <FocusedLayout title="Shop" description="Browse products">
      <div className="space-y-6" data-testid="shop-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Shop</h1>
            <p className="mt-2 text-muted-foreground">Browse and purchase products</p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9 w-64" placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="shop-search" />
          </div>
        </div>

        {loading ? (
          <Card><CardContent className="py-12 text-center">Loading...</CardContent></Card>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center space-y-3">
              <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground" />
              <p className="text-muted-foreground">No products available</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Ads at top */}
            {!adsLoading && ads.length > 0 && (
              <div className="space-y-3">
                {ads.slice(0, 2).map((ad) => (
                  <AdCard key={ad.id} ad={ad} variant="compact" />
                ))}
              </div>
            )}
            
            {/* Products Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map(p => (
                <Card key={p.id} data-testid={`product-${p.id}`} className="hover:shadow-lg transition-shadow">
                  {p.image_url && (
                    <div className="aspect-video overflow-hidden rounded-t-lg">
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{p.name}</CardTitle>
                      <Badge variant="outline">{p.stock} left</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">{p.description}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {p.price} <span className="text-sm text-muted-foreground">{p.currency}</span>
                      </p>
                      <Button size="sm" onClick={() => buy(p)} disabled={p.stock === 0} data-testid={`buy-${p.id}`}>
                        Buy Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </FocusedLayout>
  );
};
